import { boltApi } from 'store/queries/bolt-api'
import {
  GetSalesCasesResponse,
  GetSalesEventsResponse,
  GetSalesParametersPayload,
  GetSalesParametersResponse,
  OpportunitiesResponse,
  SalesOpportunitiesStatsResponse,
  SalesOpportunityPayload,
  SalesOpportunityStatsPayload
} from './types.ts'
import { TagTypes } from 'store/queries/bolt-api/types.ts'
import { Response } from 'types/request.ts'
import { stringify } from 'qs'
import { SalesOpportunity } from 'types/sales.ts'
import { filterAndSortEvents } from 'utils/sales.ts'
import { Flow } from 'types/logging.ts'
import { createErrorTransformer } from 'store/queries/utils.ts'

export const salesBoltApi = boltApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET SALES EVENTS
    getSalesEvents: builder.query<GetSalesEventsResponse, void>({
      query: () => ({
        url: '/sales/users/me/events'
      }),
      transformResponse: ({ success, data }: Response<GetSalesEventsResponse>) => {
        if (!success || !data) {
          return { events: [] }
        }

        return {
          events: filterAndSortEvents(data.events)
        }
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.SALES}:getSalesEvents]`)
    }),

    // GET SALES CASES
    getSalesCases: builder.query<GetSalesCasesResponse | null, void>({
      providesTags: [TagTypes.CASE],
      query: () => ({
        url: '/sales/cases'
      }),
      transformResponse: ({ success, data }: Response<GetSalesCasesResponse>) => {
        return success && data ? data : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.SALES}:getSalesCases]`)
    }),

    // GET SALES OPPORTUNITIES
    getSalesOpportunities: builder.query<SalesOpportunity[] | null, SalesOpportunityPayload>({
      providesTags: [TagTypes.OPPORTUNITY],
      query: ({ searchQuery, filter, from, until }) => {
        const query = stringify(
          { search: searchQuery, type: filter, from, until },
          {
            addQueryPrefix: true,
            skipNulls: true
          }
        )
        return {
          url: `/sales/opportunities${query}`
        }
      },
      transformResponse: ({ success, data }: Response<OpportunitiesResponse>) => {
        return success && data ? data.opportunities : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.SALES}:getSalesOpportunities]`)
    }),

    // GET SALES OPPORTUNITY STATS
    getSalesOpportunitiesStats: builder.query<SalesOpportunitiesStatsResponse | null, SalesOpportunityStatsPayload>({
      query: ({ from, until }) => {
        const query = stringify({ from, until }, { addQueryPrefix: true, skipNulls: true })
        return { url: `/sales/opportunities/stats${query}` }
      },
      transformResponse: ({ success, data }: Response<SalesOpportunitiesStatsResponse>) => {
        return success && data ? data : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.SALES}:getSalesOpportunitiesStats]`)
    }),

    // GET SALES PARAMETERS BY KEY
    getSalesParametersByKey: builder.query<GetSalesParametersResponse | null, GetSalesParametersPayload>({
      query: ({ key }) => ({
        url: `/sales/parameters/${key}`
      }),
      transformResponse: ({ success, data }: Response<GetSalesParametersResponse>) => {
        return success && data ? data : null
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.SALES}:getSalesParametersByKey]`)
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSalesEventsQuery,
  useGetSalesCasesQuery,
  useGetSalesOpportunitiesQuery,
  useGetSalesOpportunitiesStatsQuery,
  useGetSalesParametersByKeyQuery
} = salesBoltApi
